/* */
import React, { useMemo, useCallback, useState } from 'react';
import get from 'lodash/get';
import { isString } from 'lodash';
import { cn } from 'helpers/css';
import { Button } from 'shadcn/components/button';
import { CalendarIcon, ClockIcon, X } from 'lucide-react';
import { isDefine } from 'helpers';
import { Popover, PopoverContent, PopoverTrigger } from 'shadcn/components/popover';
import { Calendar } from 'shadcn/components/calendar';
import { momentInstance } from 'helpers/date-time';
import TimePicker from 'components/TimePicker';

const DateTimeField = ({
  field = {},
  form,
  minDate,
  maxDate,
  disabled,
  label,
  classNameContainer,
  isClearable,
  placeholder,
  disableSelectTime,
  afterOnChange,
  required,
  ...props
}) => {
  //! State
  const [open, setOpen] = useState(false);
  const { setFieldValue, touched = {}, errors = {} } = form || {};

  const value = useMemo(() => {
    const tempValue = field.value || props.value || null;

    if (isString(tempValue)) {
      return new Date(tempValue);
    }

    return tempValue;
  }, [field.value, props.value]);

  const name = useMemo(() => {
    if (field.name) {
      return field.name;
    }

    return props.name;
  }, [field.name, props.name]);

  const errorEle = get(errors, name);
  const touchedEle = get(touched, name);
  const isInvalid = touchedEle && !!errorEle;

  const format = 'DD/MM/YYYY';

  const valueByMomentObj = useMemo(() => momentInstance(value), [value]);

  const handleTime = (e) => {
    const time = e.target.value;

    const date = momentInstance(value);
    const [hours, minutes] = time.split(':').map((str) => parseInt(str, 10));
    date.set('hours', hours);
    date.set('minutes', minutes);
    date.set('seconds', 0);

    setFieldValue(name, date.toDate());
    afterOnChange && afterOnChange(date.toDate());
  };

  //! Function
  const onClickClear = useCallback(() => {
    setFieldValue(name, '');
    afterOnChange && afterOnChange('');
  }, [value]);

  const onChangeDate = useCallback(
    (date) => {
      if (!date) {
        setFieldValue(name, date);
        afterOnChange && afterOnChange(date);
        return;
      }
      const current = value ? momentInstance(value) : momentInstance();
      const next = momentInstance(date);
      next.set('hours', current.get('hours'));
      next.set('minutes', current.get('minutes'));
      next.set('seconds', current.get('seconds') || 0);
      const nextDate = next.toDate();
      setFieldValue(name, nextDate);
      afterOnChange && afterOnChange(nextDate);
    },
    [value]
  );

  const handleSelectTime = (type) => (time) => {
    const date = value ? momentInstance(value) : momentInstance();
    date.set('seconds', 0);

    if (type === 'hour') {
      date.set('hours', time);
    }

    if (type === 'minute') {
      date.set('minutes', time);
    }

    setFieldValue(name, date.toDate());
  };

  //! Render
  return (
    <div className={cn('component:DateTimeField', classNameContainer)}>
      {label && <label className={cn('!tw-mb-1 tw-block', required && 'required')}>{label}</label>}

      <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
        <PopoverTrigger asChild disabled={disabled}>
          <div
            className={cn(
              'tw-flex tw-border tw-rounded-md dark:tw-bg-[var(--bg-dark-input)] tw-overflow-hidden',
              isInvalid && 'tw-border-red-500',
              disabled && '!tw-cursor-default tw-bg-gray-100'
            )}
          >
            <Button
              variant='ghost'
              disabled={disabled}
              className={cn(
                'tw-justify-start tw-text-left tw-font-normal tw-flex-wrap md:tw-flex-nowrap tw-h-auto tw-max-h-9 md:tw-h-9 tw-w-max hover:tw-bg-inherit',
                !disableSelectTime && 'tw-border-r-0 tw-rounded-e-none',
                !value && 'tw-text-muted-foreground'
              )}
            >
              <CalendarIcon />
              {isDefine(value)
                ? momentInstance(value).format(format)
                : placeholder || 'Pick a date'}
              {disableSelectTime && isClearable && value && (
                <Button
                  disabled={disabled}
                  variant='ghost'
                  className='tw-p-1 hover:tw-bg-transparent tw-ml-1'
                  onClick={(e) => {
                    e.stopPropagation();
                    onClickClear && onClickClear();
                  }}
                >
                  <X />
                </Button>
              )}
            </Button>

            {!disableSelectTime && (
              <div
                className={cn(
                  'tw-py-1 tw-px-3 tw-border-l tw-h-9 tw-max-h-9 dark:tw-bg-primary-darkInput tw-flex tw-items-center',
                  disabled && 'tw-opacity-50 tw-bg-gray-100'
                )}
              >
                <span>{value ? momentInstance(value).format('HH:mm') : '--:--'} </span>

                <ClockIcon size={14} className='tw-ml-2' />

                {isClearable && value && (
                  <Button
                    disabled={disabled}
                    variant='ghost'
                    className='tw-p-1 hover:tw-bg-transparent tw-ml-1'
                    onClick={(e) => {
                      e.stopPropagation();
                      onClickClear && onClickClear();
                    }}
                  >
                    <X />
                  </Button>
                )}
              </div>
            )}
          </div>
        </PopoverTrigger>

        <PopoverContent
          className='tw-w-auto tw-p-0 dark:tw-bg-[var(--bg-dark)] tw-overflow-hidden'
          align='start'
        >
          <div className='tw-flex'>
            <Calendar
              mode='single'
              selected={value}
              onSelect={onChangeDate}
              disabled={
                disabled ||
                ((date) => {
                  if (isDefine(minDate) && isDefine(maxDate)) {
                    return (
                      momentInstance(date).isSameOrBefore(
                        momentInstance(minDate).subtract(1, 'days')
                      ) &&
                      momentInstance(date).isSameOrAfter(momentInstance(maxDate).add(1, 'days'))
                    );
                  }

                  if (isDefine(minDate)) {
                    return momentInstance(date).isSameOrBefore(
                      momentInstance(minDate).subtract(1, 'days')
                    );
                  }

                  if (isDefine(maxDate)) {
                    return momentInstance(date).isSameOrAfter(
                      momentInstance(maxDate).add(1, 'days')
                    );
                  }

                  return date < new Date('1900-01-01');
                })
              }
              captionLayout='dropdown'
            />

            {!disableSelectTime && (
              <TimePicker
                hour={valueByMomentObj.format('HH')}
                minute={valueByMomentObj.format('mm')}
                onClickHour={handleSelectTime('hour')}
                onClickMinute={handleSelectTime('minute')}
              />
            )}
          </div>
        </PopoverContent>
      </Popover>

      {isInvalid && <div className='invalid-feedback'>{errorEle}</div>}
    </div>
  );
};

export default DateTimeField;
